import type { AppLocale } from "@/i18n/locales";
import type { UnitSystem } from "@/state/units.state";
import { plural, t } from "@lingui/core/macro";

const METERS_PER_MILE = 1609.344;
const FEET_PER_METER = 3.280839895013123;

export function secondsToHhMm(s?: number | null) {
  if (!s) return "0m";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function formatDistance(
  meters: number,
  locale: AppLocale = "en",
  unitSystem: UnitSystem = "metric",
) {
  const nf0 = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  });

  const nf1 = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    useGrouping: true,
  });

  if (!Number.isFinite(meters) || meters <= 0) {
    return "-";
  }

  if (unitSystem === "imperial") {
    // Show feet for short distances, miles otherwise
    const feet = meters * FEET_PER_METER;
    if (feet >= 528) {
      const miles = meters / METERS_PER_MILE;
      const use1 = miles < 10;
      return `${(use1 ? nf1 : nf0).format(miles)} mi`;
    }
    return `${nf0.format(feet)} ft`;
  }

  if (meters >= 1000) return `${nf0.format(meters / 1000)} km`;
  return `${nf0.format(meters)} m`;
}

export function formatElevation(
  meters: number,
  locale: AppLocale = "en",
  unitSystem: UnitSystem = "metric",
) {
  const nf0 = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  });

  if (!Number.isFinite(meters) || meters <= 0) {
    return "-";
  }

  if (unitSystem === "imperial") {
    const feet = meters * FEET_PER_METER;
    return `${nf0.format(feet)} ft`;
  }

  return `${nf0.format(meters)} m`;
}

export function formatTime(seconds?: number | null, locale = "en") {
  if (seconds == null || seconds <= 0) return "";

  const nf = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  });

  const totalMinutes = Math.floor(seconds / 60);
  const minutes = totalMinutes % 60;

  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;

  const days = Math.floor(totalHours / 24);

  if (days > 0) {
    return hours > 0
      ? `${nf.format(days)}d ${nf.format(hours)}h`
      : `${nf.format(days)}d`;
  }

  if (totalHours > 0) {
    return minutes > 0
      ? `${nf.format(totalHours)}h ${nf.format(minutes)}m`
      : `${nf.format(totalHours)}h`;
  }

  return `${nf.format(minutes)}m`;
}

export function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}
export function isoMonth(d: Date) {
  return d.toISOString().slice(0, 7);
}
export function isoYear(d: Date) {
  return String(d.getFullYear());
}

export function localDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
export function localMonth(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}
export function localYear(date: Date): string {
  const y = date.getFullYear();
  return `${y}`;
}

/**
 * Formats an ISO date / Date for "last sync" display.
 *
 * Examples:
 * - "Just now"
 * - "5 min ago"
 * - "2h ago"
 * - "Yesterday"
 * - "Jan 12, 14:32"
 * - "Jan 12, 2025"
 */
export function formatLastSync(
  locale: string,
  d?: string | Date | null,
  now: Date = new Date(),
): string {
  if (!d) return t`Never`;

  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return t`Never`;

  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 30) return t`Just now`;

  if (diffSec < 60) {
    return plural(diffSec, {
      one: "# second ago",
      other: "# seconds ago",
    });
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return plural(diffMin, {
      one: "# min ago",
      other: "# min ago",
    });
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return plural(diffHr, {
      one: "# hour ago",
      other: "# hours ago",
    });
  }

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return t`Yesterday`;

  if (diffDay < 7) {
    return plural(diffDay, {
      one: "# day ago",
      other: "# days ago",
    });
  }

  const sameYear = date.getFullYear() === now.getFullYear();

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    ...(sameYear
      ? { hour: "2-digit", minute: "2-digit" }
      : { year: "numeric" }),
  }).format(date);
}
